import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { Card, ErrorBox, Skeleton } from "../../components/ui";
import { useAssignmentMutation, usePersonTimeline } from "../../lib/api";

const SIDE_PILL: Record<string, string> = {
  Employee: "bg-blue-50 text-brand-primary border-brand-primary/30",
  Contractor: "bg-violet-50 text-violet-700 border-violet-300",
};
const STATE_PILL: Record<string, string> = {
  Current: "bg-green-50 text-status-green", Past: "bg-brand-grey-light text-brand-grey",
  Future: "bg-amber-50 text-status-amber", Draft: "bg-brand-grey-light text-brand-charcoal",
};

/** One person's working life across employment and contracting (WFA-01 … WFA-04, OS-0622).
 *
 * Each period keeps its own history where it lives — contractor time and papers on the Contractor record,
 * payroll and compliance on the Employee — and this screen only joins them. Nothing is merged: eleven months on
 * an agency gate are not eleven months of service, and showing them as one number would make them look like it. */
export default function PersonTimeline() {
  const { t } = useTranslation();
  const { person = "" } = useParams();
  const q = usePersonTimeline(decodeURIComponent(person));
  const change = useAssignmentMutation();
  const [form, setForm] = useState<Record<string, string>>({});
  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-28" />)}</div>;
  const d = q.data;
  const p = d.person as Record<string, string | null>;
  const current = d.periods.find((x) => x.state === "Current");
  const canManage = d.permissions.can_manage;
  const field = "rounded border border-brand-grey-light px-2 py-1 text-sm";

  return (
    <div className="mt-4 space-y-6" data-wfa-person={p.name as string}>
      <Link to="/people/assignments" className="text-xs font-semibold text-brand-primary hover:underline">← {t("wfa.backToRegister")}</Link>

      <Card className="p-4">
        <h1 className="font-heading text-xl font-semibold">{p.person_name}</h1>
        <p className="mt-1 text-xs text-brand-grey">
          {[p.national_id ? `${t("wfa.nationalId")} ${p.national_id}` : t("wfa.noNationalId"),
            current ? t("wfa.currentlyOn", { side: t(`wfa.side.${current.assignment_type}`), since: current.from_date })
                    : t("wfa.notEngaged")].join(" · ")}
        </p>
      </Card>

      <Card className="overflow-hidden" data-wfa-timeline>
        <div className="border-b border-brand-grey-light px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("wfa.timeline")}</h2>
          <p className="mt-0.5 text-[11px] text-brand-grey">{t("wfa.timelineHint")}</p>
        </div>
        <ol className="divide-y divide-brand-grey-light">
          {d.periods.map((period) => (
            <li key={period.name} className="px-4 py-3" data-wfa-period={period.name}>
              {period.gap_days_before > 0 && (
                <div className="mb-2 text-[11px] italic text-status-amber">
                  {t("wfa.gap", { days: period.gap_days_before })}
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${SIDE_PILL[period.assignment_type]}`}>
                  {t(`wfa.side.${period.assignment_type}`)}
                </span>
                <span className="text-sm">
                  {period.from_date} → {period.to_date ?? t("wfa.open")}
                  <span className="ml-2 text-[11px] text-brand-grey">{t("wfa.days", { days: period.days })}</span>
                </span>
                <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${STATE_PILL[period.state] ?? ""}`}>
                  {t(`wfa.state.${period.state}`, period.state)}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-brand-grey">
                {[period.role_title, period.company, period.employee ?? period.contractor].filter(Boolean).join(" · ")}
              </div>
              {period.reason && <div className="mt-1 text-[11px] italic text-brand-grey">{period.reason}</div>}
              <div className="mt-1 text-[11px] text-brand-charcoal">
                {period.history.kind === "Contractor"
                  ? t("wfa.history.contractor", { engagements: period.history.engagements ?? 0, time: period.history.time_logs ?? 0, docs: period.history.documents ?? 0 })
                  : t("wfa.history.employee", { docs: period.history.compliance_documents ?? 0, certs: period.history.certifications ?? 0, sheets: period.history.timesheets ?? 0 })}
              </div>
            </li>
          ))}
        </ol>
      </Card>

      {canManage && current && (
        <Card className="p-4" data-wfa-change>
          <h2 className="font-heading text-base font-semibold">{t("wfa.change.title")}</h2>
          <p className="mt-1 text-[11px] text-brand-grey">{t("wfa.change.hint")}</p>

          {current.assignment_type === "Contractor" ? (
            <div className="mt-3 grid gap-2 md:grid-cols-5" data-wfa-hire>
              <label className="text-xs text-brand-grey md:col-span-1">{t("wfa.change.from")}
                <input type="date" value={form.date ?? ""} onChange={(e) => set("date", e.target.value)} className={`${field} mt-1 w-full`} /></label>
              <label className="text-xs text-brand-grey">{t("wfa.change.gender")}
                <select value={form.gender ?? ""} onChange={(e) => set("gender", e.target.value)} className={`${field} mt-1 w-full`}>
                  <option value="">–</option><option value="Female">{t("wfa.change.female")}</option><option value="Male">{t("wfa.change.male")}</option>
                </select></label>
              <label className="text-xs text-brand-grey">{t("wfa.change.dob")}
                <input type="date" value={form.dob ?? ""} onChange={(e) => set("dob", e.target.value)} className={`${field} mt-1 w-full`} /></label>
              <label className="text-xs text-brand-grey">{t("wfa.change.designation")}
                <input value={form.designation ?? ""} onChange={(e) => set("designation", e.target.value)} className={`${field} mt-1 w-full`} /></label>
              <div className="flex items-end">
                <button disabled={change.isPending || !form.date || !form.gender || !form.dob} data-wfa-do-hire
                  onClick={() => change.mutate({ method: "hire_contractor", params: {
                    contractor: current.contractor, effective_from: form.date, gender: form.gender,
                    date_of_birth: form.dob, designation: form.designation || null, reason: form.reason || null } })}
                  className="w-full rounded bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
                  {change.isPending ? t("wfa.change.working") : t("wfa.change.hire")}
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-3 grid gap-2 md:grid-cols-4" data-wfa-engage>
              <label className="text-xs text-brand-grey">{t("wfa.change.from")}
                <input type="date" value={form.date ?? ""} onChange={(e) => set("date", e.target.value)} className={`${field} mt-1 w-full`} /></label>
              <label className="text-xs text-brand-grey md:col-span-2">{t("wfa.change.trade")}
                <input value={form.trade ?? ""} onChange={(e) => set("trade", e.target.value)} className={`${field} mt-1 w-full`} /></label>
              <div className="flex items-end">
                <button disabled={change.isPending || !form.date} data-wfa-do-engage
                  onClick={() => change.mutate({ method: "engage_employee", params: {
                    employee: current.employee, effective_from: form.date,
                    trade: form.trade || null, reason: form.reason || null } })}
                  className="w-full rounded border border-violet-400 px-3 py-1.5 text-sm font-semibold text-violet-700 disabled:opacity-50">
                  {change.isPending ? t("wfa.change.working") : t("wfa.change.engage")}
                </button>
              </div>
            </div>
          )}

          <p className="mt-2 text-[11px] text-status-amber">
            {current.assignment_type === "Contractor" ? t("wfa.change.hireWarning") : t("wfa.change.engageWarning")}
          </p>
          {change.isError && <div className="mt-2"><ErrorBox error={change.error} /></div>}
        </Card>
      )}
    </div>
  );
}
